/*
  industrialkonzept Next, part 3b (2026-09-23)
  - Centimetres or inches everywhere on the page (display only)
  - Fit check: does it fit into your space? Drawn to scale next to a person
  - Room preview: place the product into a photo of your own room (the photo stays on the device)
  Needs ik-lab.js (loaded first). Optional: without this script the shop works as before.
*/
(() => {
  if (window.__iklTools || !window.__ikl) return;
  window.__iklTools = true;
  const { root, template, T, esc, sized, store, icon, openSheet, sheetHead, onBoard, pinKey } = window.__ikl;

  /* =====================================================================
     4. Centimetres or inches, everywhere on the page
     Only the display changes, the shop data stays as it is. Every number followed by
     cm or mm is converted, including runs like "22x14,3x6,5cm" or "H77.5 X W50 cm" and ranges like
     "90-180cm", "120–180 cm" or "90 to 180 cm",
     wherever it stands: variant names, descriptions, spec fields, titles.
     Numbers without a unit are left alone. Switching back restores the original text.
     ===================================================================== */
  const UNIT_KEY = 'ikn-unit';
  const country = ((window.Shopify && window.Shopify.country) || '').toUpperCase();
  const unit = () => {
    const saved = store.get(UNIT_KEY, null);
    if (saved === 'in' || saved === 'cm') return saved;
    return ['US', 'LR', 'MM'].includes(country) ? 'in' : 'cm';
  };
  const inchFmt = new Intl.NumberFormat(root.lang || undefined, { maximumFractionDigits: 1 });
  const NUM = '\\d+(?:[.,]\\d+)?';
  // numbers of one measurement are joined by x × * / (22x14x6), or form a range with a dash or "bis"/"to"
  // (90-180cm, 90 to 180 cm), which is extendable furniture
  const JOIN = '(?:\\s*[x×*/–—-]\\s*|\\s+(?:bis|to)\\s+)';
  const RUN = new RegExp(
    '(' + NUM + ')(?:\\s*(?:cm|mm))?((?:' + JOIN + '(?:[a-zø⌀]{1,2}\\s*:?\\s*)?' + NUM + '(?:\\s*(?:cm|mm))?)*)\\s*(cm|mm)\\b',
    'gi'
  );
  const RANGE = new RegExp('(' + NUM + ')(?:\\s*(?:cm|mm))?(?:\\s*[–—-]\\s*|\\s+(?:bis|to)\\s+)(' + NUM + ')', 'gi');
  const toNumber = (t) => parseFloat(t.replace(',', '.'));
  const convertRun = (match, u) => {
    const factor = u.toLowerCase() === 'mm' ? 0.1 / 2.54 : 1 / 2.54;
    // German texts use the decimal comma: "14,3" is one number, never a list
    return match
      .replace(new RegExp(NUM, 'g'), (n) => inchFmt.format(toNumber(n) * factor))
      .replace(/\s*(cm|mm)\b/gi, '')
      .trimEnd() + ' in';
  };
  const convertText = (text) =>
    String(text || '').replace(/(\d),(\d{3})(?=\s*mm)/g, '$1$2').replace(RUN, (match, first, rest, u) => {
      // a range goes up (90-180); "2024-180 cm" is a year or a code before a size: then only the size
      // right before the unit is converted, as before
      const falling = [...match.matchAll(RANGE)].some((m) => toNumber(m[2]) < toNumber(m[1]));
      if (!falling) return convertRun(match, u);
      const cut = match.search(new RegExp(NUM + '\\s*(?:cm|mm)\\s*$', 'i'));
      return match.slice(0, cut) + convertRun(match.slice(cut), u);
    });
  const originals = new WeakMap();
  const SKIP = 'SCRIPT,STYLE,NOSCRIPT,TEXTAREA,INPUT,SELECT,OPTION,[data-ikn-no-units]';
  let busyUnits = false;
  const applyUnits = () => {
    const inch = unit() === 'in';
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    const nodes = [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) nodes.push(n);
    busyUnits = true;
    nodes.forEach((n) => {
      const parent = n.parentElement;
      if (!parent || parent.closest(SKIP)) return;
      const original = originals.has(n) ? originals.get(n) : n.nodeValue;
      if (inch) {
        if (!/\d\s*(cm|mm)\b/i.test(original)) return;
        const converted = convertText(original);
        if (converted !== original) {
          originals.set(n, original);
          if (n.nodeValue !== converted) n.nodeValue = converted;
          if (!parent.title) parent.title = original.trim();
        }
      } else if (originals.has(n)) {
        n.nodeValue = original;
        originals.delete(n);
        if (parent.title === original.trim()) parent.removeAttribute('title');
      }
    });
    busyUnits = false;
    document.querySelectorAll('[data-ikl-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-ikl-unit') === unit())));
  };
  if (unit() === 'in') applyUnits();
  let unitTimer = 0;
  new MutationObserver((records) => {
    if (busyUnits || unit() !== 'in') return;
    // Text the theme writes again (variant switch, cart) is converted again
    records.forEach((r) => {
      if (r.type === 'characterData' && originals.has(r.target) && r.target.nodeValue !== convertText(originals.get(r.target))) originals.delete(r.target);
    });
    window.clearTimeout(unitTimer);
    unitTimer = window.setTimeout(applyUnits, 120);
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
  const unitSwitch = () =>
    '<span class="ikl-unit" role="group" aria-label="' + esc(T('tools.unit')) + '" data-ikn-no-units>' +
    '<button type="button" data-ikl-unit="cm" aria-pressed="' + (unit() === 'cm') + '">cm</button>' +
    '<button type="button" data-ikl-unit="in" aria-pressed="' + (unit() === 'in') + '">in</button></span>';
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-ikl-unit]');
    if (!b) return;
    store.set(UNIT_KEY, b.getAttribute('data-ikl-unit'));
    applyUnits();
    document.dispatchEvent(new CustomEvent('ikl:unit'));
  });

  /* =====================================================================
     5. Product page tools: fit check, room preview, board
     ===================================================================== */
  const dataEl = document.querySelector('[data-ikn-product]');
  let product = null;
  try {
    product = dataEl ? JSON.parse(dataEl.textContent) : null;
  } catch (e) {
    product = null;
  }
  if (!product || template !== 'product') return;

  // Manufacturer measurements come in many notations; read them into centimetres.
  // sure: the text says which number is the height. Three bare numbers ("22x14,3x6,5cm")
  // do not, so the height is later taken from the shape of the product photo.
  const parseDims = (raw) => {
    if (!raw) return null;
    // "1,000mm" is a thousands separator, "14,3" a decimal comma
    let s = String(raw).toLowerCase().replace(/(\d),(\d{3})(?=\s*mm)/g, '$1$2').replace(/(\d),(\d)/g, '$1.$2').replace(/[×*]/g, 'x');
    if (/variab/.test(s)) return null;
    // Several sizes listed ("L: … , M: …"): use the first one
    s = s.split(/;|\n/)[0];
    const inMm = /\d\s*mm\b/.test(s) && !/\d\s*cm\b/.test(s);
    const f = inMm ? 0.1 : 1;
    const N = '(\\d+(?:\\.\\d+)?)';
    const pick = (re) => {
      const m = s.match(re);
      return m ? parseFloat(m[1]) * f : null;
    };
    const before = '(?:^|[^a-wyzäöü])';
    const label = (words, after) =>
      pick(new RegExp(before + '(?:' + words + ')\\s*[:.]?\\s*' + N)) || pick(new RegExp(N + '\\s*(?:cm|mm)?\\s*(?:' + after + ')'));
    let h = label('height|höhe|h', 'high|hoch');
    let w = label('width|breite|w|b', 'wide|breit');
    let d = label('depth|tiefe|d|t', 'deep|tief');
    const len = label('length|länge|l', 'long|lang');
    // "length" is the long side: the width when no width is given, otherwise the depth
    if (len) {
      if (!w) w = len;
      else if (!d) d = len;
    }
    let sure = !!(h && w);
    const dia = pick(new RegExp('(?:ø|⌀|diameter|durchmesser)\\s*[:.]?\\s*' + N));
    let round = false;
    if (dia) {
      round = true;
      w = w || dia;
      d = d || dia;
      if (!h) {
        const m = s.match(new RegExp('(?:ø|⌀)\\s*' + N + '\\s*(?:cm|mm)?\\s*x\\s*h?\\s*' + N));
        if (m) h = parseFloat(m[2]) * f;
      }
      sure = !!h;
    }
    let vals = null;
    if (!h || !w) {
      const m = s.match(new RegExp(N + '\\s*(?:cm|mm)?\\s*x\\s*[a-z]?\\s*' + N + '\\s*(?:cm|mm)?\\s*x\\s*[a-z]?\\s*' + N));
      if (m) {
        vals = [parseFloat(m[1]) * f, parseFloat(m[2]) * f, parseFloat(m[3]) * f];
        // An order spelled out in the text, e.g. "(width x depth x height)", is reliable
        const order = s.match(/\(([a-zäöü]+)\s*x\s*([a-zäöü]+)\s*x\s*([a-zäöü]+)\)/);
        if (order) {
          const kind = (word) => (/^(h|height|höhe)$/.test(word) ? 'h' : /^(w|width|breite|b)$/.test(word) ? 'w' : 'd');
          const k = order.slice(1).map(kind);
          const get = (key) => vals[k.indexOf(key)];
          if (k.includes('h')) {
            const flat = vals.filter((v, i) => k[i] !== 'h');
            h = h || get('h');
            w = w || Math.max(...flat);
            d = d || Math.min(...flat);
            sure = true;
          }
        } else {
          sure = !!h;
          w = w || vals[0];
          d = d || vals[1];
          h = h || vals[2];
        }
      }
    }
    if (!h || !w || h > 600 || w > 600 || h < 1 || w < 1) return null;
    return { w, d: d && d <= 600 ? d : null, h, round, sure, vals: sure ? null : vals };
  };
  // With bare numbers: stand the piece so that its front matches the photo
  const orientations = (vals) =>
    [0, 1, 2].map((k) => {
      const rest = vals.filter((v, i) => i !== k);
      return { h: vals[k], w: Math.max(...rest), d: Math.min(...rest), k };
    });
  const fromPhoto = (dm, cut) => {
    if (!dm || dm.sure || !dm.vals || !cut) return dm;
    const aspect = cut.canvas.width / cut.canvas.height;
    const err = (front, height) => Math.abs(Math.log(aspect) - Math.log(front / height));
    const score = (o) => {
      const a = err(o.w, o.h);
      const b = err(o.d, o.h);
      return a <= b ? { e: a, w: o.w, d: o.d } : { e: b, w: o.d, d: o.w };
    };
    // Keep the manufacturer's order unless the photo clearly disagrees
    const given = { h: dm.vals[2], w: dm.vals[0], d: dm.vals[1] };
    const givenErr = Math.min(err(given.w, given.h), err(given.d, given.h));
    let best = null;
    orientations(dm.vals).forEach((o) => {
      const sc = score(o);
      if (!best || sc.e < best.e) best = { ...sc, h: o.h, k: o.k };
    });
    if (best && best.e < givenErr - 0.4) return { ...dm, w: best.w, d: best.d, h: best.h };
    return dm;
  };
  const dims = parseDims(product.dims);
  // "3-Pack", "Set of 4", "2er-Set": the measurements are for one piece
  const packMatch = String(product.title).match(/(\d+)\s*[-‑ ]?\s*(?:pack|pcs|pieces|er[- ]?(?:set|pack)|teilig|stück)\b|set of (\d+)/i);
  const pack = packMatch ? +(packMatch[1] || packMatch[2]) : 1;

  // Tool row below the add to cart button
  const addBtn = document.querySelector('#MainContent .product-block-area .add-to-cart');
  const anchor = addBtn && (addBtn.closest('.product-block-area > *') || addBtn.closest('form'));
  if (anchor) {
    const row = document.createElement('div');
    row.className = 'ikl-tools';
    row.innerHTML =
      (dims ? '<button type="button" class="ikl-tool" data-ikl-fit>' + icon.ruler + '<span>' + esc(T('tools.fit')) + '</span></button>' : '') +
      '<button type="button" class="ikl-tool" data-ikl-room>' + icon.room + '<span>' + esc(T('tools.room')) + '</span></button>' +
      // pins the variant chosen right now (assets/ik-lab.js, pinKey)
      '<button type="button" class="ikl-tool" data-ikl-heart="' + esc(product.handle) + '" data-ikl-variant aria-pressed="' + onBoard(pinKey(product.handle)) + '">' + icon.pin +
      '<span data-ikl-heart-text>' + esc(onBoard(pinKey(product.handle)) ? T('lab.on_your_board') : T('lab.save_to_board')) + '</span></button>' +
      unitSwitch();
    anchor.insertAdjacentElement('afterend', row);
    row.addEventListener('click', (e) => {
      if (e.target.closest('[data-ikl-fit]')) openFit();
      if (e.target.closest('[data-ikl-room]')) openRoom();
    });
  }

  const addProductToCart = () => {
    const bar = document.querySelector('[data-ikn-buybar-btn]');
    if (bar) bar.click();
    else if (addBtn) addBtn.click();
  };

  /* ---------- Fit check ---------- */
  const openFit = () => {
    const inch = () => unit() === 'in';
    const len = (cmValue) => inchFmt.format(inch() ? cmValue / 2.54 : cmValue);
    const withUnit = (cmValue) => len(cmValue) + (inch() ? ' in' : ' cm');
    const d = openSheet(
      'ikl-fit',
      sheetHead(T('tools.fit_label'), T('tools.fit_title')) +
      '<div class="ikl-fit__body">' +
      '<div class="ikl-fit__draw" data-ikl-draw></div>' +
      '<div class="ikl-fit__side">' +
      '<p class="ikl-fit__dims"><span class="ikh-label">' + esc(product.title) + '</span><span data-ikl-dims></span></p>' +
      (pack > 1
        ? '<p class="ikl-fit__unsure">' + esc(T('tools.pack_note', { n: pack })) + '</p>'
        : '') +
      (dims.sure
        ? ''
        : '<p class="ikl-fit__unsure">' + esc(T('tools.unsure_height')) +
          '<span class="ikl-unit" style="display:flex;width:max-content;margin-top:0.6rem" data-ikl-hchips></span></p>') +
      '<div class="ikl-fit__unitrow">' + unitSwitch() + '</div>' +
      '<p class="ikl-fit__ask">' + esc(T('tools.ask_space')) + '</p>' +
      '<div class="ikl-fit__inputs">' +
      [['w', T('tools.width')], ['d', T('tools.depth')], ['h', T('tools.height')]]
        .map(([k, label]) => '<label class="ikl-field"><span>' + esc(label) + '</span><span class="ikl-field__box"><input type="number" inputmode="decimal" min="0" step="0.5" data-k="' + k + '" value="" placeholder="–"><span data-ikl-u>cm</span></span></label>')
        .join('') +
      '</div>' +
      '<ul class="ikl-fit__verdict" data-ikl-verdict aria-live="polite"></ul>' +
      '<p class="ikl-fit__note">' + esc(T('tools.maker_dims')) + ' <span data-ikl-note></span></p>' +
      '</div></div>'
    );
    const drawEl = d.querySelector('[data-ikl-draw]');
    const verdictEl = d.querySelector('[data-ikl-verdict]');
    const dimsEl = d.querySelector('[data-ikl-dims]');
    d.setAttribute('data-ikn-no-units', '');
    let D = { ...dims };
    const showDims = () => {
      const u = inch() ? ' in' : ' cm';
      const chips = d.querySelector('[data-ikl-hchips]');
      if (chips && D.vals) {
        chips.innerHTML = orientations(D.vals)
          .map((o) => '<button type="button" data-ikl-h="' + o.k + '" aria-pressed="' + (o.h === D.h) + '">' + len(o.h) + u + '</button>')
          .join('');
      }
      dimsEl.textContent = D.round
        ? T('tools.dims_round', { w: len(D.w), h: len(D.h) }) + u
        : D.d
          ? T('tools.dims_wdh', { w: len(D.w), d: len(D.d), h: len(D.h) }) + u
          : T('tools.dims_wh', { w: len(D.w), h: len(D.h) }) + u;
      d.querySelectorAll('[data-ikl-u]').forEach((x) => (x.textContent = inch() ? 'in' : 'cm'));
      d.querySelector('[data-ikl-note]').textContent = inch() ? convertText(product.dims) : product.dims;
    };
    // The fields show the chosen unit; everything is kept in centimetres
    const fillInputs = () => {
      const saved = store.get('ikn-space', {});
      d.querySelectorAll('input[data-k]').forEach((i) => {
        const v = saved[i.getAttribute('data-k')];
        i.value = v ? String(Math.round((inch() ? v / 2.54 : v) * 10) / 10) : '';
      });
    };
    fillInputs();
    showDims();

    const update = () => {
      const s = {};
      d.querySelectorAll('input[data-k]').forEach((i) => {
        const v = parseFloat(String(i.value).replace(',', '.'));
        if (v > 0) s[i.getAttribute('data-k')] = inch() ? v * 2.54 : v;
      });
      store.set('ikn-space', s);

      // Drawing to scale, 1 unit = 1 cm. The product is drawn as a plain body in its
      // own measurements, never as the photo: photos often show more than the product
      // (a TV on the stand, three holders in a pack).
      const pw = D.w;
      const ph = D.h;
      const pd = D.d || 0;
      const sw = s.w || 0;
      const sh = s.h || 0;
      // Small things next to a wine bottle, furniture next to a person
      const small = Math.max(ph, pw) < 60;
      const ref = small ? { h: 30, w: 8 } : { h: 175, w: 45 };
      const dx = D.round ? 0 : pd * 0.45;
      const dy = D.round ? pw * 0.14 : pd * 0.26;
      const H0 = Math.max(ref.h, ph + dy, sh);
      const fs = H0 * 0.055;
      const gap = Math.max(ref.w * 0.8, pw * 0.25);
      const rx = gap * 0.6;
      const x0 = rx + ref.w + gap;
      const areaW = Math.max(pw + dx, sw);
      const vbW = x0 + areaW + gap + fs * 2;
      const topPad = fs * 2.6;
      const floor = topPad + H0;
      const vbH = floor + fs * 2.2;
      const stroke = 'vector-effect="non-scaling-stroke"';
      const svg = [];
      svg.push('<svg viewBox="0 0 ' + vbW + ' ' + vbH + '" role="img" aria-label="' + esc(T('tools.drawing')) + '" font-family="inherit">');
      // reference
      if (small) {
        const bx = rx;
        const f = floor;
        svg.push('<path fill="#cfcfcf" d="M' + (bx + 2.7) + ' ' + (f - 30) + 'H' + (bx + 4.8) + 'V' + (f - 21) +
          'C' + (bx + 4.8) + ' ' + (f - 19) + ' ' + (bx + 7.5) + ' ' + (f - 18.5) + ' ' + (bx + 7.5) + ' ' + (f - 16) +
          'V' + (f - 0.6) + 'Q' + (bx + 7.5) + ' ' + f + ' ' + (bx + 6.9) + ' ' + f + 'H' + (bx + 0.6) + 'Q' + bx + ' ' + f + ' ' + bx + ' ' + (f - 0.6) +
          'V' + (f - 16) + 'C' + bx + ' ' + (f - 18.5) + ' ' + (bx + 2.7) + ' ' + (f - 19) + ' ' + (bx + 2.7) + ' ' + (f - 21) + 'Z"/>');
        svg.push('<text fill="#6f6f6f" x="' + (bx + 3.75) + '" y="' + (floor - ref.h - fs * 0.6) + '" font-size="' + fs + '" text-anchor="middle">' + esc(T('tools.bottle', { size: withUnit(30) })) + '</text>');
      } else {
        const px = rx;
        svg.push('<g fill="#cfcfcf"><circle cx="' + (px + 22) + '" cy="' + (floor - 175 + 11) + '" r="11"/>' +
          '<rect x="' + (px + 8) + '" y="' + (floor - 175 + 25) + '" width="28" height="72" rx="12"/>' +
          '<rect x="' + (px + 1) + '" y="' + (floor - 175 + 29) + '" width="7" height="62" rx="3.5"/>' +
          '<rect x="' + (px + 36) + '" y="' + (floor - 175 + 29) + '" width="7" height="62" rx="3.5"/>' +
          '<rect x="' + (px + 10) + '" y="' + (floor - 84) + '" width="11" height="84" rx="5"/>' +
          '<rect x="' + (px + 23) + '" y="' + (floor - 84) + '" width="11" height="84" rx="5"/></g>');
        svg.push('<text fill="#6f6f6f" x="' + (px + 22) + '" y="' + (floor - 175 - fs * 0.6) + '" font-size="' + fs + '" text-anchor="middle">' + (inch() ? '5′9″' : new Intl.NumberFormat(root.lang || undefined, { minimumFractionDigits: 2 }).format(1.75) + ' m') + '</text>');
      }
      // the space the visitor entered
      if (sw || sh) {
        const w = sw || areaW + gap;
        const top = sh ? floor - sh : topPad * 0.4;
        svg.push('<rect x="' + (x0 - (sw ? 0 : gap * 0.3)) + '" y="' + top + '" width="' + w + '" height="' + (floor - top) + '" fill="rgba(23,2,250,0.05)" stroke="#1702fa" stroke-width="1.5" stroke-dasharray="6 4" ' + stroke + '/>');
        if (sw) svg.push('<text fill="#1702fa" x="' + (x0 + sw / 2) + '" y="' + (top - fs * 0.5) + '" font-size="' + fs + '" text-anchor="middle">' + esc(T('tools.your_space', { size: len(sw) + (sh ? ' × ' + len(sh) : '') + (inch() ? ' in' : ' cm') })) + '</text>');
      }
      // the product as a plain body
      const fits = (!sw || pw <= sw || (pd && pd <= sw)) && (!sh || ph <= sh);
      const line = fits ? '#000' : '#d0021b';
      // a cylinder stands on the floor with the lowest point of its base
      const base = D.round ? floor - pw * 0.14 : floor;
      const top = base - ph;
      if (D.round) {
        const ry = pw * 0.14;
        svg.push('<path d="M' + x0 + ' ' + top + 'V' + base + 'a' + pw / 2 + ' ' + ry + ' 0 0 0 ' + pw + ' 0V' + top + '" fill="#e6e6e6" stroke="' + line + '" stroke-width="1.25" ' + stroke + '/>');
        svg.push('<ellipse cx="' + (x0 + pw / 2) + '" cy="' + top + '" rx="' + pw / 2 + '" ry="' + ry + '" fill="#f4f4f4" stroke="' + line + '" stroke-width="1.25" ' + stroke + '/>');
      } else {
        if (dx) {
          svg.push('<path d="M' + x0 + ' ' + top + 'l' + dx + ' ' + -dy + 'h' + pw + 'l' + -dx + ' ' + dy + 'z" fill="#f4f4f4" stroke="' + line + '" stroke-width="1.25" ' + stroke + '/>');
          svg.push('<path d="M' + (x0 + pw) + ' ' + top + 'l' + dx + ' ' + -dy + 'v' + ph + 'l' + -dx + ' ' + dy + 'z" fill="#d9d9d9" stroke="' + line + '" stroke-width="1.25" ' + stroke + '/>');
        }
        svg.push('<rect x="' + x0 + '" y="' + top + '" width="' + pw + '" height="' + ph + '" fill="#e6e6e6" stroke="' + line + '" stroke-width="1.25" ' + stroke + '/>');
      }
      svg.push('<text x="' + (x0 + pw / 2) + '" y="' + (floor + fs * 1.4) + '" font-size="' + fs + '" text-anchor="middle">' + (D.round ? '⌀ ' : '') + withUnit(pw) + '</text>');
      svg.push('<text x="' + (x0 + pw / 2) + '" y="' + (top + ph / 2) + '" font-size="' + fs + '" text-anchor="middle" dominant-baseline="middle">' + esc(T('tools.drawing_height', { h: len(ph) })) + '</text>');
      svg.push('<line x1="0" y1="' + floor + '" x2="' + vbW + '" y2="' + floor + '" stroke="#000" stroke-width="1" ' + stroke + '/>');
      svg.push('</svg>');
      drawEl.innerHTML = svg.join('');

      // Verdict: width and depth may be swapped by turning the piece
      const out = [];
      const say = (ok, text) => out.push('<li class="' + (ok ? 'is-ok' : 'is-bad') + '"><span aria-hidden="true">' + (ok ? '✓' : '✕') + '</span>' + esc(text) + '</li>');
      const cm = (v) => withUnit(Math.abs(v));
      if (s.w || s.d) {
        const spaceW = s.w || Infinity;
        const spaceD = s.d || Infinity;
        const pd = D.d || 0;
        const straight = pw <= spaceW && pd <= spaceD;
        const turned = pd && pd <= spaceW && pw <= spaceD;
        if (straight) {
          if (s.w) say(true, T(spaceW - pw < 3 ? 'tools.width_fits_tight' : 'tools.width_fits', { v: cm(spaceW - pw) }));
          if (s.d && pd) say(true, T('tools.depth_fits', { v: cm(spaceD - pd) }));
          if (s.d && !pd) say(true, T('tools.depth_unknown'));
        } else if (turned) {
          say(true, T('tools.fits_turned'));
        } else {
          if (pw > spaceW) say(false, T('tools.too_wide', { v: cm(pw - spaceW) }));
          if (pd && pd > spaceD) say(false, T('tools.too_deep', { v: cm(pd - spaceD) }));
        }
      }
      if (s.h) {
        if (ph <= s.h) say(true, T('tools.height_fits', { v: cm(s.h - ph) }));
        else say(false, T('tools.too_tall', { v: cm(ph - s.h) }));
      }
      // Bare numbers: say so when another way of standing it would fit
      if (!D.sure && D.vals && out.some((x) => x.includes('is-bad'))) {
        const alt = orientations(D.vals).find(
          (o) => (!s.h || o.h <= s.h) && ((o.w <= (s.w || Infinity) && o.d <= (s.d || Infinity)) || (o.d <= (s.w || Infinity) && o.w <= (s.d || Infinity)))
        );
        if (alt) say(true, T('tools.fits_other_way', { v: withUnit(alt.h) }));
      }
      if (!out.length) out.push('<li class="is-hint">' + esc(T('tools.enter_hint')) + '</li>');
      verdictEl.innerHTML = out.join('');
    };
    d.addEventListener('input', update);
    const onUnit = () => {
      fillInputs();
      showDims();
      update();
    };
    document.addEventListener('ikl:unit', onUnit);
    d.addEventListener('close', () => document.removeEventListener('ikl:unit', onUnit));
    d.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-ikl-h]');
      if (!chip || !D.vals) return;
      const next = orientations(D.vals)[+chip.getAttribute('data-ikl-h')];
      D = { ...D, w: next.w, d: next.d, h: next.h };
      showDims();
      update();
    });
    update();
    // With bare numbers, the photo suggests which side is up (the visitor can change it)
    getSprite().then((cut) => {
      if (!cut || !d.open) return;
      D = fromPhoto(D, cut);
      showDims();
      update();
    });
  };

  /* ---------- Room preview ---------- */
  // Remove a light, even background from a packshot (flood fill from the edges)
  const cutOut = (img) => {
    const max = 900;
    const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * k));
    const h = Math.max(1, Math.round(img.naturalHeight * k));
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    let data;
    try {
      data = ctx.getImageData(0, 0, w, h);
    } catch (e) {
      return null;
    }
    const px = data.data;
    const bg = new Uint8Array(w * h);
    const isBg = (i) => {
      const o = i * 4;
      const r = px[o], g = px[o + 1], b = px[o + 2];
      return r > 226 && g > 226 && b > 226 && Math.max(r, g, b) - Math.min(r, g, b) < 20;
    };
    const stack = new Int32Array(w * h);
    let top = 0;
    const push = (i) => {
      if (!bg[i] && isBg(i)) {
        bg[i] = 1;
        stack[top++] = i;
      }
    };
    for (let x = 0; x < w; x++) {
      push(x);
      push((h - 1) * w + x);
    }
    for (let y = 0; y < h; y++) {
      push(y * w);
      push(y * w + w - 1);
    }
    while (top) {
      const i = stack[--top];
      const x = i % w;
      if (x > 0) push(i - 1);
      if (x < w - 1) push(i + 1);
      if (i >= w) push(i - w);
      if (i < w * (h - 1)) push(i + w);
    }
    let count = 0;
    let minX = w, minY = h, maxX = 0, maxY = 0;
    for (let i = 0; i < w * h; i++) {
      const o = i * 4;
      if (bg[i]) {
        px[o + 3] = 0;
        count++;
        continue;
      }
      const x = i % w;
      const y = (i / w) | 0;
      // soften the edge next to the removed background
      if ((x > 0 && bg[i - 1]) || (x < w - 1 && bg[i + 1]) || (i >= w && bg[i - w]) || (i < w * (h - 1) && bg[i + w])) px[o + 3] = 170;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    ctx.putImageData(data, 0, 0);
    if (maxX <= minX || maxY <= minY) return null;
    const out = document.createElement('canvas');
    out.width = maxX - minX + 1;
    out.height = maxY - minY + 1;
    out.getContext('2d').drawImage(c, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
    return { canvas: out, removed: count / (w * h) };
  };
  const loadImage = (src) =>
    new Promise((resolve) => {
      const i = new Image();
      i.crossOrigin = 'anonymous';
      i.onload = () => resolve(i);
      i.onerror = () => resolve(null);
      i.src = src;
    });
  // Use the picture with the cleanest background among the first ones
  let spritePromise = null;
  const getSprite = () => {
    if (spritePromise) return spritePromise;
    spritePromise = (async () => {
      let best = null;
      for (const src of (product.images || []).slice(0, 4)) {
        const img = await loadImage(sized(src, 1000));
        if (!img) continue;
        const cut = cutOut(img);
        if (cut && (!best || cut.removed > best.removed)) best = cut;
        if (best && best.removed > 0.35) break;
      }
      return best;
    })();
    return spritePromise;
  };

  const openRoom = () => {
    const d = openSheet(
      'ikl-room',
      sheetHead(T('tools.room_label'), T('tools.room_title', { title: product.title })) +
      '<div class="ikl-room__stage" data-ikl-stage>' +
      '<canvas data-ikl-canvas></canvas>' +
      '<div class="ikl-room__start" data-ikl-start>' +
      '<p>' + esc(T('tools.room_intro')) + '</p>' +
      '<div class="ikl-room__startbtns"><label class="ikl-btn ikl-btn--light">' + esc(T('tools.choose_photo')) + '<input type="file" accept="image/*" data-ikl-file hidden></label>' +
      '<button type="button" class="ikl-btn ikl-btn--line" data-ikl-demo>' + esc(T('tools.try_without_photo')) + '</button></div>' +
      '</div>' +
      '<p class="ikl-room__hint" data-ikl-hint hidden>' + esc(T('tools.room_hint')) + '</p>' +
      '</div>' +
      '<div class="ikl-room__bar">' +
      '<label class="ikl-room__size"><span>' + esc(T('tools.size')) + '</span><input type="range" min="8" max="95" value="42" data-ikl-size></label>' +
      '<button type="button" class="ikl-btn ikl-btn--line" data-ikl-flip>' + esc(T('tools.flip')) + '</button>' +
      '<label class="ikl-btn ikl-btn--line">' + esc(T('tools.other_photo')) + '<input type="file" accept="image/*" data-ikl-file hidden></label>' +
      '<button type="button" class="ikl-btn ikl-btn--line" data-ikl-save>' + esc(T('tools.save_picture')) + '</button>' +
      '<button type="button" class="ikl-btn ikl-btn--light" data-ikl-cart>' + esc(T('tools.add_to_cart')) + '</button>' +
      '</div>'
    );
    const stage = d.querySelector('[data-ikl-stage]');
    const canvas = d.querySelector('[data-ikl-canvas]');
    const ctx = canvas.getContext('2d');
    const size = d.querySelector('[data-ikl-size]');
    const st = { bg: null, demo: false, x: 0.5, y: 0.86, s: 0.42, flip: false, ready: false };
    let sprite = null;

    const fitCanvas = () => {
      const r = stage.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
      canvas.style.width = r.width + 'px';
      canvas.style.height = r.height + 'px';
      render();
    };
    const drawDemoRoom = (W, H) => {
      const wall = ctx.createLinearGradient(0, 0, 0, H * 0.72);
      wall.addColorStop(0, '#efece7');
      wall.addColorStop(1, '#e4e0d9');
      ctx.fillStyle = wall;
      ctx.fillRect(0, 0, W, H * 0.72);
      const floor = ctx.createLinearGradient(0, H * 0.72, 0, H);
      floor.addColorStop(0, '#cfc6b8');
      floor.addColorStop(1, '#b9ae9d');
      ctx.fillStyle = floor;
      ctx.fillRect(0, H * 0.72, W, H * 0.28);
      ctx.fillStyle = '#f7f5f1';
      ctx.fillRect(0, H * 0.72 - H * 0.018, W, H * 0.018);
    };
    const render = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      if (st.bg) {
        const k = Math.max(W / st.bg.naturalWidth, H / st.bg.naturalHeight);
        const bw = st.bg.naturalWidth * k;
        const bh = st.bg.naturalHeight * k;
        ctx.drawImage(st.bg, (W - bw) / 2, (H - bh) / 2, bw, bh);
      } else if (st.demo) {
        drawDemoRoom(W, H);
      } else {
        return;
      }
      if (!sprite) return;
      const sh = st.s * H;
      const sw = sh * (sprite.width / sprite.height);
      const cx = st.x * W;
      const by = st.y * H;
      // soft contact shadow
      ctx.save();
      ctx.filter = 'blur(' + Math.max(4, sw * 0.03) + 'px)';
      ctx.fillStyle = 'rgba(0,0,0,0.28)';
      ctx.beginPath();
      ctx.ellipse(cx, by - sh * 0.01, sw * 0.46, Math.max(4, sw * 0.055), 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      ctx.save();
      ctx.translate(cx, by);
      if (st.flip) ctx.scale(-1, 1);
      ctx.drawImage(sprite, -sw / 2, -sh, sw, sh);
      ctx.restore();
    };
    const start = async () => {
      d.querySelector('[data-ikl-start]').hidden = true;
      d.querySelector('[data-ikl-hint]').hidden = false;
      d.classList.add('is-live');
      fitCanvas();
      const cut = await getSprite();
      sprite = cut ? cut.canvas : null;
      if (!sprite) {
        const hint = d.querySelector('[data-ikl-hint]');
        hint.textContent = T('tools.no_cutout');
      }
      st.ready = true;
      render();
    };
    d.addEventListener('change', (e) => {
      const input = e.target.closest('[data-ikl-file]');
      if (!input || !input.files || !input.files[0]) return;
      const url = URL.createObjectURL(input.files[0]);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url); // the picture stays drawn, the memory for the file is freed
        st.bg = img;
        st.demo = false;
        start();
      };
      img.src = url;
    });
    d.addEventListener('click', (e) => {
      if (e.target.closest('[data-ikl-demo]')) {
        st.demo = true;
        start();
      }
      if (e.target.closest('[data-ikl-flip]')) {
        st.flip = !st.flip;
        render();
      }
      if (e.target.closest('[data-ikl-save]') && st.ready) {
        // small credit in the corner, then download
        render();
        const W = canvas.width;
        const H = canvas.height;
        ctx.save();
        ctx.font = Math.round(H * 0.022) + 'px sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.shadowColor = 'rgba(0,0,0,0.35)';
        ctx.shadowBlur = 6;
        ctx.fillText('industrialkonzept · ' + product.title, H * 0.03, H - H * 0.03);
        ctx.restore();
        canvas.toBlob((blob) => {
          if (!blob) return;
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = 'industrialkonzept-' + product.handle + '.png';
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.setTimeout(() => URL.revokeObjectURL(a.href), 4000);
          render();
        }, 'image/png');
      }
      if (e.target.closest('[data-ikl-cart]')) {
        d.close();
        addProductToCart();
      }
    });
    size.addEventListener('input', () => {
      st.s = size.value / 100;
      render();
    });

    // drag, wheel and pinch
    const pointers = new Map();
    let pinch = null;
    canvas.addEventListener('pointerdown', (e) => {
      if (!st.ready) return;
      canvas.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), s: st.s };
      }
    });
    canvas.addEventListener('pointermove', (e) => {
      const prev = pointers.get(e.pointerId);
      if (!prev) return;
      const r = canvas.getBoundingClientRect();
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2 && pinch) {
        const [a, b] = [...pointers.values()];
        st.s = Math.max(0.08, Math.min(0.95, pinch.s * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.dist)));
        size.value = Math.round(st.s * 100);
      } else if (pointers.size === 1) {
        st.x = Math.max(0, Math.min(1, st.x + (e.clientX - prev.x) / r.width));
        st.y = Math.max(0.1, Math.min(1.1, st.y + (e.clientY - prev.y) / r.height));
      }
      render();
    });
    const up = (e) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinch = null;
    };
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    canvas.addEventListener(
      'wheel',
      (e) => {
        if (!st.ready) return;
        e.preventDefault();
        st.s = Math.max(0.08, Math.min(0.95, st.s * (e.deltaY < 0 ? 1.06 : 0.94)));
        size.value = Math.round(st.s * 100);
        render();
      },
      { passive: false }
    );
    window.addEventListener('resize', fitCanvas, { passive: true });
    d.addEventListener('close', () => window.removeEventListener('resize', fitCanvas));
    // start cutting out while the visitor is still choosing a photo
    getSprite();
  };
})();
